import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';

type AppThemeMode = 'dark' | 'light';

interface AppThemeTokens {
  bgPage: string;
  bgCard: string;
  textPrimary: string;
  textSecondary: string;
  textAccent: string;
  border: string;
  bgPageTw: string;
  bgCardTw: string;
  textPrimaryTw: string;
  textSecondaryTw: string;
  borderTw: string;
}

const DARK_TOKENS: AppThemeTokens = {
  bgPage: '#12100E',
  bgCard: '#1F1A17',
  textPrimary: '#EDE9E5',
  textSecondary: '#9E8E82',
  textAccent: '#FB8C3C',
  border: '#372E27',
  bgPageTw: 'bg-[#12100E]',
  bgCardTw: 'bg-[#1F1A17]',
  textPrimaryTw: 'text-[#EDE9E5]',
  textSecondaryTw: 'text-[#9E8E82]',
  borderTw: 'border-[#372E27]',
};

const LIGHT_TOKENS: AppThemeTokens = {
  bgPage: '#F9F7F4',
  bgCard: '#FFFFFF',
  textPrimary: '#302318',
  textSecondary: '#7E7168',
  textAccent: '#F97316',
  border: '#EAE6E1',
  bgPageTw: 'bg-[#F9F7F4]',
  bgCardTw: 'bg-white',
  textPrimaryTw: 'text-[#302318]',
  textSecondaryTw: 'text-[#7E7168]',
  borderTw: 'border-[#EAE6E1]',
};

interface AppThemeContextType {
  theme: AppThemeMode;
  tokens: AppThemeTokens;
  isDark: boolean;
  toggleTheme: () => void;
}

const AppThemeContext = createContext<AppThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'app-theme';

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<AppThemeMode>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem(STORAGE_KEY) as AppThemeMode) || 'light';
    }
    return 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  const isDark = theme === 'dark';
  const tokens = isDark ? DARK_TOKENS : LIGHT_TOKENS;

  const value = useMemo(() => ({ theme, tokens, isDark, toggleTheme }), [theme, tokens, isDark]);

  return (
    <AppThemeContext.Provider value={value}>
      {children}
    </AppThemeContext.Provider>
  );
}

export function useAppTheme() {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return context;
}

// Legacy aliases
export const DriverThemeProvider = AppThemeProvider;
export const useDriverTheme = useAppTheme;
